/**
 * YouTube URL helpers.
 *
 * Thumbnails are derived straight from the video id — `img.youtube.com` needs
 * no API key and no quota, which matters because the YouTube Data API quota is
 * the real ceiling on this project. Titles come from YouTube's public oEmbed
 * endpoint (`fetchVideoTitle`), which is likewise key-free, unmetered and
 * CORS-enabled — so we never have to spend Data API quota just to name a video.
 * `displayTitle` is the synchronous fallback (the id) shown until oEmbed lands.
 */

/** Extract the video id from the URL forms YouTube actually hands out. */
export function videoId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1) || null;
    if (u.hostname.endsWith("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      // /embed/ID, /shorts/ID, /live/ID
      const m = u.pathname.match(/^\/(embed|shorts|live)\/([^/?]+)/);
      if (m) return m[2];
    }
    return null;
  } catch {
    return null;
  }
}

export function thumbnailUrl(url: string): string | null {
  const id = videoId(url);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
}

/** Best label we can show synchronously (before oEmbed resolves the real title). */
export function displayTitle(url: string): string {
  return videoId(url) ?? url;
}

/**
 * Resolve a video's real title via YouTube's oEmbed endpoint — no API key, no
 * quota, CORS-enabled so the browser can call it directly. Returns null for
 * anything it can't name (deleted/private video, non-YouTube URL, network
 * error); callers fall back to `displayTitle`.
 */
export async function fetchVideoTitle(url: string): Promise<string | null> {
  const id = videoId(url);
  if (!id) return null;
  try {
    const endpoint = `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(
      `https://www.youtube.com/watch?v=${id}`,
    )}`;
    const res = await fetch(endpoint);
    if (!res.ok) return null;
    const data = (await res.json()) as { title?: string };
    return data.title ?? null;
  } catch {
    return null;
  }
}

export const formatMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/** Seconds → YouTube-style clock ("3:07", "1:02:10"). */
export function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
